import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { Panel } from '../design-system/panel.tsx'
import {
  AccessRequestError,
  SIGN_IN_PATH,
  accessForPath,
  fetchAccess,
  normalizePath,
  type AccessView,
  type PageAccess
} from '../lib/access.ts'

export const ACCESS_QUERY_KEY = ['page-access'] as const

/**
 * The one read of the access API. Everything on screen — the navigation, the
 * route guard, the controls on /access — comes from this single answer, so a
 * change made in one place shows up everywhere at once.
 */
export function useAccess() {
  return useQuery<AccessView, AccessRequestError>({
    queryKey: ACCESS_QUERY_KEY,
    queryFn: fetchAccess,
    retry: false
  })
}

function GuardNotice({
  testId,
  title,
  children
}: {
  testId: string
  title: string
  children?: ReactNode
}) {
  return (
    <Panel data-testid={testId} title={title} className="mx-auto">
      <div className="text-muted-foreground text-sm">{children}</div>
    </Panel>
  )
}

function Problem({ error }: { error: AccessRequestError }) {
  if (error.status === 401) {
    return (
      <GuardNotice testId="access-signed-out" title="Sign in to continue">
        <p>The internal platform needs to know who you are.</p>
        <a
          data-testid="access-sign-in-link"
          className="mt-4 inline-block underline"
          href="/sign-in"
        >
          Go to sign in
        </a>
      </GuardNotice>
    )
  }

  if (error.status === 503) {
    return (
      <GuardNotice
        testId="access-unconfigured"
        title="Access control is not set up"
      >
        <p>{error.message}</p>
        {error.details.length > 0 ? (
          <p className="mt-2 font-mono text-xs">{error.details.join(', ')}</p>
        ) : null}
      </GuardNotice>
    )
  }

  return (
    <GuardNotice testId="access-error" title="Access could not be checked">
      <p>{error.message}</p>
    </GuardNotice>
  )
}

function Denied({
  page,
  superUsers
}: {
  page: PageAccess
  superUsers: string[]
}) {
  return (
    <GuardNotice testId="access-denied" title={`You cannot open ${page.title}`}>
      <p>
        Ask <span data-testid="access-denied-owner">{page.owner}</span> to add
        you.
      </p>
      {superUsers.length > 0 ? (
        <p className="mt-2">
          A platform super user can also help: {superUsers.join(', ')}.
        </p>
      ) : null}
    </GuardNotice>
  )
}

/**
 * Decides whether the route below it may be drawn.
 *
 * This is the navigation guard, not the enforcement. A person who reads the
 * bundle can render any screen they like; what keeps data safe is that the
 * backend applies the same rules to every request the screen makes.
 *
 * A path with no registered page is refused. A route nobody declared has no
 * owner and no answer to who may see it, so there is nothing to allow.
 */
export function AccessGuard({
  pathname,
  children
}: {
  pathname: string
  children: ReactNode
}) {
  const { data, error, isPending } = useAccess()
  const isSignIn = normalizePath(pathname) === SIGN_IN_PATH

  if (isSignIn) {
    return children
  }

  if (isPending) {
    return (
      <p
        data-testid="access-checking"
        className="text-sm text-muted-foreground"
      >
        Checking access…
      </p>
    )
  }

  if (error || !data) {
    return (
      <Problem
        error={
          error ??
          new AccessRequestError(0, 'the access service could not be reached')
        }
      />
    )
  }

  const match = accessForPath(data, pathname)

  if (match === 'platform') {
    return children
  }

  if (!match) {
    return (
      <GuardNotice
        testId="access-unregistered"
        title="This page is not registered"
      >
        <p>
          Nobody has said who may see{' '}
          <span className="font-mono">{pathname}</span>. Add it to the page
          registry in{' '}
          <span className="font-mono">backend/src/config/page-registry.js</span>
          .
        </p>
      </GuardNotice>
    )
  }

  if (!match.canView) {
    return <Denied page={match} superUsers={data.superUsers} />
  }

  return children
}
